import React, { useState, useEffect } from 'react';
import { FileText, Building2, Lightbulb, MapPin, Phone, Mail, X, ArrowDown, Newspaper, BookOpen, CheckCircle, Target, UserCheck, ExternalLink } from 'lucide-react';

// --- Data Perusahaan ---
const companyInfo = {
    name: "PT Visi Pencerah Nusantara",
    tagline: "Membuka Jendela Informasi, Mencerahkan Wawasan Bangsa.",
    description: "PT Visi Pencerah Nusantara adalah perusahaan yang bergerak di bidang penerbitan, berdedikasi untuk menyajikan informasi berkualitas melalui surat kabar, jurnal, buletin, dan berbagai media cetak maupun digital lainnya. Kami berkomitmen untuk menjadi sumber wawasan yang terpercaya dan mencerahkan bagi masyarakat.",
    registrationNumber: "AHU-008156.AH.01.30.Tahun 2024",
    nib: "0802240008664",
    personInCharge: "Inka Nusamuda Pratama",
    vision: "Menjadi perusahaan penerbitan terdepan yang inovatif dan inspiratif, serta berkontribusi aktif dalam pencerdasan kehidupan bangsa.",
    mission: "Menyediakan konten yang akurat, edukatif, dan relevan. Mengadopsi teknologi terkini dalam proses penerbitan. Membangun jaringan kemitraan yang luas untuk penyebaran informasi.",
    journalPortalUrl: "https://visipencerahnusantara.my.id/",

    // Layanan diperbarui sesuai KBLI
    // coba
    services: [
        {
            icon: <Newspaper size={40} className="text-teal-500" />,
            title: "Penerbitan Berkala",
            description: "Produksi dan distribusi surat kabar, jurnal ilmiah, buletin komunitas, dan majalah secara profesional (KBLI: 58130)."
        },
        {
            icon: <BookOpen size={40} className="text-teal-500" />,
            title: "Aktivitas Penerbitan Lainnya",
            description: "Menyediakan layanan penerbitan buku, direktori, materi promosi, dan konten digital lainnya sesuai kebutuhan (KBLI: 58190)."
        },
        {
            icon: <CheckCircle size={40} className="text-teal-500" />,
            title: "Manajemen Konten",
            description: "Pengembangan dan pengelolaan konten berkualitas untuk berbagai platform media, dari penulisan hingga publikasi."
        }
    ],

    // Dokumen diperbarui dengan nama file dari folder public
    legalDocuments: [
        {
            title: "Sertifikat Pendirian",
            description: "Sertifikat Pendaftaran Pendirian Perseroan Perorangan dari Kemenkumham.",
            url: "/surat-sertifikat-41240208520109794%202.pdf"
        },
        {
            title: "Surat Pernyataan",
            description: "Dokumen pernyataan pendirian perseroan perorangan.",
            url: "/surat-pernyataan-41240208520109794.pdf"
        },
        {
            title: "Pernyataan Mandiri",
            description: "Dokumen pernyataan mandiri terkait pendirian PT Visi Pencerah Nusantara.",
            url: "/PERNYATAAN%20MANDIRI%20PT.%20VISI%20PENCERAH%20NUSANTARA.pdf"
        },
        {
            title: "Nomor Induk Berusaha (NIB)",
            description: "Identitas resmi sebagai pelaku usaha yang terdaftar dalam sistem OSS.",
            url: "/NIB%20PT.%20VISI%20PENCERAH%20NUSANTARA%202.pdf"
        },
        {
            title: "Pernyataan Mandiri K3L",
            description: "Komitmen perusahaan terhadap Keselamatan, Kesehatan Kerja, dan Lingkungan.",
            url: "/K3L%20PT.%20VISI%20PENCERAH%20NUSANTARA.pdf"
        }
    ],

    contact: {
        address: "Jl. Lalu Mesir, Turida, Sandubaya, Kota Mataram, NTB 83237",
        phone: "0878-1380-0777",
        email: "visipencerahnusantara@gmail.com"
    },
};

// --- Komponen untuk Modal PDF Viewer ---
const PdfViewerModal = ({ pdfUrl, docTitle, onClose }) => {
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, []);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-50 p-4 animate-fade-in-fast">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl h-[90vh] flex flex-col overflow-hidden">
                <div className="flex justify-between items-center p-4 border-b">
                    <h3 className="text-lg font-semibold text-gray-800">{docTitle}</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-red-600 transition-colors p-2 rounded-full">
                        <X size={24} />
                    </button>
                </div>
                <div className="flex-grow">
                    <iframe
                        src={pdfUrl}
                        title={docTitle}
                        className="w-full h-full border-0"
                    >
                        Maaf, browser Anda tidak mendukung tampilan PDF. Silakan unduh file untuk melihatnya.
                    </iframe>
                </div>
            </div>
        </div>
    );
};

// --- Komponen Utama Aplikasi ---
export default function App() {
    const [pdfToShow, setPdfToShow] = useState(null);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 10);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleShowPdf = (doc) => {
        setPdfToShow(doc);
    };

    const handleClosePdf = () => {
        setPdfToShow(null);
    };

    // Style tambahan untuk animasi dan kelancaran scroll
    useEffect(() => {
        const styleSheet = document.createElement("style");
        styleSheet.type = "text/css";
        styleSheet.innerText = `
            html { scroll-behavior: smooth; }
            @keyframes fade-in {
                from { opacity: 0; transform: translateY(20px); }
                to { opacity: 1; transform: translateY(0); }
            }
            .animate-fade-in { animation: fade-in 0.8s ease-out forwards; }
            .animate-fade-in-fast { animation: fade-in 0.3s ease-out forwards; }
        `;
        document.head.appendChild(styleSheet);
        return () => {
            document.head.removeChild(styleSheet);
        };
    }, []);

    return (
        <div className="bg-gray-50 font-sans text-gray-700 antialiased">
            {pdfToShow && <PdfViewerModal pdfUrl={pdfToShow.url} docTitle={pdfToShow.title} onClose={handleClosePdf} />}

            {/* --- Header / Navigation --- */}
            <header className={`sticky top-0 z-40 transition-all duration-300 ${scrolled ? 'bg-white/95 shadow-md backdrop-blur-sm' : 'bg-transparent'}`}>
                <div className="container mx-auto px-6 py-4 flex justify-between items-center">
                    <div className="flex items-center space-x-3">
                        <Building2 className="text-teal-600" size={28} />
                        <h1 className={`text-xl font-bold tracking-tight transition-colors ${scrolled ? 'text-gray-800' : 'text-white md:text-gray-800'}`}>{companyInfo.name}</h1>
                    </div>
                    <nav className="hidden md:flex space-x-8 items-center font-medium">
                        <a href="#tentang" className={`transition-colors ${scrolled ? 'text-gray-600 hover:text-teal-600' : 'text-white hover:text-teal-200 md:text-gray-600 md:hover:text-teal-600'}`}>Tentang</a>
                        <a href="#layanan" className={`transition-colors ${scrolled ? 'text-gray-600 hover:text-teal-600' : 'text-white hover:text-teal-200 md:text-gray-600 md:hover:text-teal-600'}`}>Layanan</a>
                        <a href="#portal-jurnal" className={`transition-colors ${scrolled ? 'text-gray-600 hover:text-teal-600' : 'text-white hover:text-teal-200 md:text-gray-600 md:hover:text-teal-600'}`}>Portal Jurnal</a>
                        <a href="#legalitas" className={`transition-colors ${scrolled ? 'text-gray-600 hover:text-teal-600' : 'text-white hover:text-teal-200 md:text-gray-600 md:hover:text-teal-600'}`}>Legalitas</a>
                        <a href="#kontak" className={`transition-colors ${scrolled ? 'text-gray-600 hover:text-teal-600' : 'text-white hover:text-teal-200 md:text-gray-600 md:hover:text-teal-600'}`}>Kontak</a>
                    </nav>
                </div>
            </header>

            <main>
                {/* --- Hero Section --- */}
                <section
                    id="hero"
                    className="relative h-screen flex flex-col items-center justify-center text-white text-center overflow-hidden bg-cover bg-center"
                    style={{ backgroundImage: "url('https://images.unsplash.com/photo-1481627834876-b7833e8f5570?q=80&w=1854&auto=format&fit=crop')" }}
                >
                    <div className="absolute inset-0 bg-black/60"></div>
                    <div className="relative z-10 p-6 flex flex-col items-center animate-fade-in">
                        <h2 className="text-4xl md:text-6xl font-extrabold tracking-tight drop-shadow-lg mb-4">
                            {companyInfo.name}
                        </h2>
                        <p className="mt-2 text-lg md:text-2xl max-w-3xl mx-auto drop-shadow-md mb-8">
                            {companyInfo.tagline}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <a
                                href="#tentang"
                                className="bg-white text-teal-700 font-bold py-3 px-8 rounded-full hover:bg-gray-100 transition-all transform hover:scale-105 shadow-lg text-lg"
                            >
                                Jelajahi Lebih Lanjut
                            </a>
                            <a
                                href={companyInfo.journalPortalUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-teal-600 hover:bg-teal-700 text-white font-bold py-3 px-8 rounded-full transition-all transform hover:scale-105 shadow-lg text-lg flex items-center justify-center gap-2"
                            >
                                Kunjungi Portal Jurnal <ExternalLink size={20} />
                            </a>
                        </div>
                    </div>
                    <a href="#tentang" className="absolute bottom-10 z-10 animate-bounce">
                        <ArrowDown size={32} />
                    </a>
                </section>

                {/* --- Tentang Kami --- */}
                <section id="tentang" className="py-20 bg-white">
                    <div className="container mx-auto px-6">
                        <div className="text-center">
                            <h3 className="text-3xl font-bold text-gray-800 mb-4">Tentang Kami</h3>
                            <p className="max-w-3xl mx-auto text-lg leading-relaxed text-gray-600">
                                {companyInfo.description}
                            </p>
                        </div>
                        <div className="mt-16 grid md:grid-cols-2 gap-12 items-center">
                            <div className="space-y-6">
                                <div>
                                    <div className="flex items-center gap-3">
                                        <Target className="text-teal-500" size={28} />
                                        <h4 className="text-2xl font-semibold text-gray-800">Visi Kami</h4>
                                    </div>
                                    <p className="mt-2 text-gray-600 leading-relaxed">{companyInfo.vision}</p>
                                </div>
                                <div>
                                    <div className="flex items-center gap-3">
                                        <Lightbulb className="text-teal-500" size={28} />
                                        <h4 className="text-2xl font-semibold text-gray-800">Misi Kami</h4>
                                    </div>
                                    <p className="mt-2 text-gray-600 leading-relaxed">{companyInfo.mission}</p>
                                </div>
                            </div>
                            <div className="bg-gray-100 p-8 rounded-xl shadow-md">
                                <h4 className="text-2xl font-semibold text-gray-800 mb-6">Informasi Perusahaan</h4>
                                <ul className="space-y-4 text-gray-700">
                                    <li className="flex items-start">
                                        <UserCheck className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                                        <span><strong>Penanggung Jawab:</strong> {companyInfo.personInCharge}</span>
                                    </li>
                                    <li className="flex items-start">
                                        <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                                        <span><strong>NIB:</strong> {companyInfo.nib}</span>
                                    </li>
                                    <li className="flex items-start">
                                        <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                                        <span><strong>No. Pendaftaran:</strong> {companyInfo.registrationNumber}</span>
                                    </li>
                                    <li className="flex items-start">
                                        <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                                        <span><strong>Lokasi:</strong> Berkedudukan di Kota Mataram, NTB</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </section>

                {/* --- Layanan Unggulan --- */}
                <section id="layanan" className="py-20 bg-gray-50">
                    <div className="container mx-auto px-6">
                        <h3 className="text-3xl font-bold text-center text-gray-800 mb-12">Layanan Unggulan Kami</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                            {companyInfo.services.map((service, index) => (
                                <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 text-center flex flex-col justify-between">
                                    <div>
                                        <div className="inline-block p-4 bg-teal-100 rounded-full mb-4">
                                            {service.icon}
                                        </div>
                                        <h4 className="text-xl font-semibold text-gray-800 mb-2">{service.title}</h4>
                                        <p className="text-gray-600">{service.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* --- SEKSI BARU: Portal Jurnal Kami (Call to Action) --- */}
                <section id="portal-jurnal" className="py-20 bg-teal-700 text-white relative overflow-hidden">
                    <div className="absolute inset-0 opacity-10 bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?q=80&w=1973&auto=format&fit=crop')" }}></div>
                    <div className="container mx-auto px-6 text-center relative z-10 max-w-4xl">
                        <span className="bg-teal-600 text-teal-100 text-xs font-semibold tracking-wider uppercase px-4 py-1.5 rounded-full mb-4 inline-block">Sistem Informasi Ilmiah</span>
                        <h3 className="text-3xl md:text-4xl font-bold mb-6">Portal Jurnal PT Visi Pencerah Nusantara</h3>
                        <p className="text-lg text-teal-100 mb-8 leading-relaxed">
                            Kami menyediakan platform publikasi ilmiah digital terpercaya untuk membagikan riset, inovasi, dan kajian akademis. Akses berbagai jurnal terbitan kami untuk berkontribusi nyata dalam perkembangan ilmu pengetahuan bangsa.
                        </p>
                        <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
                            <a
                                href={companyInfo.journalPortalUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-white text-teal-800 font-bold py-3.5 px-8 rounded-full hover:bg-gray-100 transition-all transform hover:scale-105 shadow-xl flex items-center gap-2 text-lg"
                            >
                                Kunjungi Portal Jurnal <ExternalLink size={20} className="text-teal-700" />
                            </a>
                        </div>
                    </div>
                </section>

                {/* --- Legalitas --- */}
                <section id="legalitas" className="py-20 bg-white">
                    <div className="container mx-auto px-6">
                        <div className="text-center max-w-3xl mx-auto mb-16">
                            <h3 className="text-3xl font-bold text-gray-800 mb-4">Dokumen Legalitas</h3>
                            <p className="text-lg text-gray-600 leading-relaxed">
                                Kami beroperasi dengan transparansi penuh. Silakan tinjau dokumen legalitas kami untuk informasi mendetail.
                            </p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 justify-center">
                            {companyInfo.legalDocuments.map((doc, index) => (
                                <div key={index} className="bg-white rounded-xl shadow-lg p-6 flex flex-col items-center text-center transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 border border-gray-100">
                                    <div className="p-5 bg-teal-100 rounded-full mb-5">
                                        <FileText size={32} className="text-teal-600" />
                                    </div>
                                    <h4 className="text-xl font-semibold text-gray-800 mb-2">{doc.title}</h4>
                                    <p className="text-gray-600 flex-grow mb-6">{doc.description}</p>
                                    <button
                                        onClick={() => handleShowPdf(doc)}
                                        className="mt-auto bg-teal-600 text-white font-bold py-2 px-6 rounded-full hover:bg-teal-700 transition-transform transform hover:scale-105 shadow-md"
                                    >
                                        Lihat Dokumen
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* --- Hubungi Kami --- */}
                <section id="kontak" className="py-20 bg-gray-800 text-white">
                    <div className="container mx-auto px-6 text-center">
                        <h3 className="text-3xl font-bold mb-4">Hubungi Kami</h3>
                        <p className="max-w-xl mx-auto text-lg text-gray-300 mb-10">
                            Kami siap untuk berdiskusi dan berkolaborasi. Hubungi kami melalui detail di bawah ini.
                        </p>
                        <div className="max-w-4xl mx-auto grid md:grid-cols-3 gap-8 text-left">
                            <div className="bg-gray-700/50 p-6 rounded-lg flex items-center gap-4">
                                <MapPin size={40} className="text-teal-400 flex-shrink-0" />
                                <div>
                                    <h4 className="font-semibold">Alamat</h4>
                                    <p className="text-gray-300 text-sm">{companyInfo.contact.address}</p>
                                </div>
                            </div>
                            <div className="bg-gray-700/50 p-6 rounded-lg flex items-center gap-4">
                                <Phone size={40} className="text-teal-400 flex-shrink-0" />
                                <div>
                                    <h4 className="font-semibold">Telepon</h4>
                                    <p className="text-gray-300 text-sm">{companyInfo.contact.phone}</p>
                                </div>
                            </div>
                            <div className="bg-gray-700/50 p-6 rounded-lg flex items-center gap-4">
                                <Mail size={40} className="text-teal-400 flex-shrink-0" />
                                <div>
                                    <h4 className="font-semibold">Email</h4>
                                    <p className="text-gray-300 text-sm">{companyInfo.contact.email}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            <footer className="bg-gray-900 text-white py-6">
                <div className="container mx-auto px-6 text-center">
                    <p>&copy; {new Date().getFullYear()} {companyInfo.name}. Semua Hak Cipta Dilindungi.</p>
                </div>
            </footer>
        </div>
    );
}
